import React, { useEffect, useState, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * In-app fullscreen image lightbox. Never opens a raw browser tab.
 * items: [{ url, kind, name }]  — only image items are shown.
 * Supports prev/next, zoom, swipe (mobile), keyboard, counter.
 */
export default function MediaLightbox({ items = [], index = 0, onClose }) {
  const images = items.filter((i) => i.kind === "image" || (i.content_type || "").startsWith("image/"));
  const startIdx = Math.max(0, Math.min(index, images.length - 1));
  const [cur, setCur] = useState(startIdx);
  const [zoom, setZoom] = useState(1);
  const touch = useRef({ x: 0, y: 0 });

  const go = useCallback((d) => {
    setZoom(1);
    setCur((c) => (c + d + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  if (!images.length) return null;
  const item = images[cur];

  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    if (zoom === 1 && Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="media-lightbox"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex flex-col"
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <div className="flex items-center justify-between px-4 py-3 text-white/80">
          <div className="text-sm font-mono" data-testid="lightbox-counter">
            {images.length > 1 ? `Image ${cur + 1} / ${images.length}` : ""}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setZoom((z) => Math.min(4, z + 0.5))} className="p-2 rounded-full glass glass-hover" data-testid="lightbox-zoom-in"><ZoomIn className="w-4 h-4" /></button>
            <button onClick={() => setZoom((z) => Math.max(1, z - 0.5))} className="p-2 rounded-full glass glass-hover" data-testid="lightbox-zoom-out"><ZoomOut className="w-4 h-4" /></button>
            <a href={item.url} download target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="p-2 rounded-full glass glass-hover" data-testid="lightbox-download"><Download className="w-4 h-4" /></a>
            <button onClick={onClose} className="p-2 rounded-full glass glass-hover" data-testid="lightbox-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center relative overflow-hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {images.length > 1 && (
            <button onClick={() => go(-1)} className="absolute left-3 z-10 p-3 rounded-full glass glass-hover" data-testid="lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
          )}
          <motion.img
            key={cur}
            src={item.url}
            alt={item.name || ""}
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: zoom }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="max-h-[80vh] max-w-[92vw] object-contain select-none cursor-zoom-in"
            style={{ transform: `scale(${zoom})` }}
            onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2))}
            draggable={false}
          />
          {images.length > 1 && (
            <button onClick={() => go(1)} className="absolute right-3 z-10 p-3 rounded-full glass glass-hover" data-testid="lightbox-next"><ChevronRight className="w-6 h-6" /></button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
